import { useCallback } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  ActivityValue as ActivityValueType,
  FlatActivityModel,
  NestableGroupValue,
  StatusClass
} from 'types/shared'
import { getProperty } from 'utils/helpers/object'
import ActivitySafeHTML from '../../ActivityInput/ActivitySafeHTML/ActivitySafeHTML'
import { useReviewContext } from '../context'
import ActivityDocumentFile from './ActivityDocumentFile'
import ActivityDocumentPhoto from './ActivityDocumentPhoto'
import ActivityDocumentVideo from './ActivityDocumentVideo'
import ActivityGroup from './ActivityGroup'
import ActivityValue from './ActivityValue'
import ActivityDocumentAudio from './ActivityDocumentAudio'
import ActivityMarkPhoto from './ActivityMarkPhoto'

interface ActivityProps {
  activityModelIds: string[]
  parentPath?: string[]
}

const Activity = ({ activityModelIds, parentPath = [] }: ActivityProps) => {
  const { flatData } = useReviewContext()
  const { t } = useTranslation()
  const activityModels = flatData?.activity_models || {}
  const activityValues = flatData?.activity_values || {}

  const visibleActivityModelIds = activityModelIds.filter(
    (activityModelId) => !activityModels[activityModelId]?.hide_in_review
  )

  const getDisplayValue = useCallback(
    (
      activityModel: FlatActivityModel,
      activityValue?: ActivityValueType | NestableGroupValue
    ) => {
      if (
        activityValue ||
        activityModel?.content_type === 'agreement_term' ||
        activityModel.content_type === 'draw'
      )
        return activityValue

      if (
        activityModel.content_type === 'document_photo' ||
        activityModel.content_type === 'mark_photo'
      ) {
        return JSON.stringify({
          des: t('step.review.empty-state.document_photo')
        })
      }

      if (activityModel.content_type === 'document_file') {
        return JSON.stringify({
          des: t('step.review.empty-state.document_file')
        })
      }

      if (activityModel.content_type === 'document_video') {
        return JSON.stringify({
          des: t('step.review.empty-state.document_video')
        })
      }

      if (activityModel.content_type === 'document_audio') {
        return JSON.stringify({
          des: t('step.review.empty-state.document_audio')
        })
      }

      return t('step.review.empty-state.common')
    },
    [t]
  )

  return (
    <>
      {visibleActivityModelIds?.map((activityModelId) => {
        const activityModel = activityModels[activityModelId]

        const isActivityGroup = activityModel.content_type === 'activity_group'
        const isDocumentActivity =
          activityModel.content_type === 'document_photo' ||
          activityModel.content_type === 'document_audio' ||
          activityModel.content_type === 'document_file' ||
          activityModel.content_type === 'document_video' ||
          activityModel.content_type === 'mark_photo'

        const activityValue = getProperty<
          ActivityValueType | NestableGroupValue
        >(activityValues, [...parentPath, activityModelId])

        const statusClass = activityValue
          ? undefined
          : ('attention' as StatusClass)
        const displayValue = getDisplayValue(activityModel, activityValue)

        return (
          <div key={activityModel.uuid}>
            {activityModel.content_type === 'safe_html' && (
              <ActivitySafeHTML htmlContent={activityModel.html_content} />
            )}

            {
              <>
                {activityModel.content_type === 'document_photo' && (
                  <ActivityDocumentPhoto
                    values={displayValue as string}
                    statusClass={statusClass}
                    hideDescription={activityModel.hide_description}
                  />
                )}

                {activityModel.content_type === 'document_video' && (
                  <ActivityDocumentVideo
                    values={displayValue as string}
                    statusClass={statusClass}
                    hideDescription={activityModel.hide_description}
                  />
                )}

                {activityModel.content_type === 'document_file' && (
                  <ActivityDocumentFile
                    values={displayValue as string}
                    label={activityModel.display_text.des}
                    statusClass={statusClass}
                    hideDescription={activityModel.hide_description}
                  />
                )}

                {activityModel.content_type === 'document_audio' && (
                  <ActivityDocumentAudio
                    values={displayValue as string}
                    statusClass={statusClass}
                    hideDescription={activityModel.hide_description}
                  />
                )}

                {activityModel.content_type === 'mark_photo' && (
                  <ActivityMarkPhoto
                    values={displayValue as string}
                    statusClass={statusClass}
                    hideDescription={activityModel.hide_description}
                    markColor={activityModel.mark_color}
                  />
                )}

                {!isDocumentActivity && !Array.isArray(displayValue) && (
                  <ActivityValue
                    uuid={activityModel.uuid}
                    label={activityModel.display_text.des}
                    value={displayValue}
                    contentType={activityModel.content_type}
                    required={activityModel.required}
                    statusClass={statusClass}
                    activityModel={activityModel}
                  />
                )}
              </>
            }

            {isActivityGroup && Array.isArray(displayValue) && (
              <ActivityGroup
                groups={displayValue}
                activityModel={activityModel}
                activityModels={activityModels}
                parentPath={[...parentPath, activityModelId]}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

export default Activity
